"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Search } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { localizeHref } from "@/core/i18n/routing";
import type { SiteNavbarContent } from "@/shared/types/site-shell-content";

interface SiteNavbarProps {
  className?: string;
  content: SiteNavbarContent;
}

export function SiteNavbar({ className, content }: SiteNavbarProps) {
  const {
    activeLocale,
    homeAriaLabel,
    homeHref,
    items,
    localeOptions,
    loginHref,
    loginLabel,
    logoAlt,
    logoUrl,
    switchLocaleLabel,
  } = content;

  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const currentY = window.scrollY;
      const isScrolled = currentY > 20;
      const isScrollingUp = currentY < lastScrollY.current;

      setScrolled(isScrolled);
      // hide when scrolling up (but keep visible at top)
      setHidden(isScrollingUp && isScrolled);

      lastScrollY.current = currentY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={cn(
        "fixed inset-x-0 top-0 z-[100] transition-all duration-300",
        scrolled
          ? "border-b border-white/10 bg-black/40 shadow-[0_4px_24px_rgba(0,0,0,0.15)] backdrop-blur-xl"
          : "bg-transparent",
        hidden ? "-translate-y-full" : "translate-y-0",
        className,
      )}
    >
      <div className="mx-auto flex w-full max-w-[1500px] items-center justify-between gap-x-6 px-6 py-4 text-white">
        <Link
          aria-label={homeAriaLabel}
          className="shrink-0 transition-transform active:scale-95"
          href={homeHref}
        >
          <Image
            alt={logoAlt}
            className="h-8 w-auto brightness-0 invert md:h-9"
            height={36}
            priority
            src={logoUrl}
            width={135}
          />
        </Link>

        <nav
          aria-label="Primary"
          className="hidden items-center justify-center gap-8 text-sm font-medium text-white/80 md:flex"
        >
          {items.map((item) => {
            const isActive =
              pathname === item.href ||
              (item.href.includes("faqs") && pathname.includes("faqs")) ||
              (item.href.includes("blogs") && pathname.includes("blogs")) ||
              (item.href.includes("assessment") && pathname.includes("assessment")) ||
              (item.href.includes("services") && pathname.includes("services"));

            return (
              <Link
                className={cn(
                  "relative py-1 transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:bg-secondary after:transition-all after:duration-300 hover:text-secondary",
                  isActive
                    ? "text-secondary after:w-full"
                    : "text-white/80 after:w-0 hover:after:w-full",
                )}
                href={item.href}
                key={item.href}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            aria-label={activeLocale === "hi" ? "खोजें" : "Search"}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/12 bg-white/8 text-white/80 transition-all duration-300 hover:scale-105 hover:border-white/30 hover:text-white"
            href={localizeHref(activeLocale, "/search")}
          >
            <Search className="h-[18px] w-[18px]" />
          </Link>

          <div className="hidden items-center gap-2 md:flex">
            <span className="text-[11px] font-medium tracking-[0.22em] text-white/60 uppercase">
              {switchLocaleLabel}
            </span>
            <div className="inline-flex rounded-full border border-white/12 bg-white/8 p-1">
              {localeOptions.map((option) => {
                const isActive = option.locale === activeLocale;

                return (
                  <Link
                    className={cn(
                      "rounded-full px-3 py-1 text-xs font-semibold transition-colors",
                      isActive ? "bg-white text-slate-950" : "text-white/76 hover:text-white",
                    )}
                    href={option.href}
                    key={option.locale}
                    lang={option.locale}
                  >
                    {option.label}
                  </Link>
                );
              })}
            </div>
          </div>

          <Link
            className="relative inline-flex h-8 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-full border border-transparent bg-secondary px-6 py-2.5 text-sm font-medium whitespace-nowrap text-secondary-foreground shadow-[0_0_15px_rgba(125,226,248,0.15)] transition-all duration-300 outline-none hover:scale-105 hover:bg-white hover:text-primary hover:shadow-[0_0_20px_rgba(255,255,255,0.3)] focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            href={loginHref}
          >
            {loginLabel}
          </Link>
        </div>
      </div>
    </header>
  );
}
